import React, { useState } from 'react';
import Modal from '../common/Modal';
import Input from '../common/Input';
import Button from '../common/Button';
import { TeamService } from '../../services/team.service';
import toast from 'react-hot-toast';
import { useAppSelector } from '../../hooks/useRedux';

interface CreateTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  onTeamCreated: () => void;
  editMode?: boolean;
  teamData?: any;
  isDepartment?: boolean;
  parentTeamId?: string;
}

const CreateTeamModal: React.FC<CreateTeamModalProps> = ({
  isOpen,
  onClose,
  onTeamCreated,
  editMode = false,
  teamData,
  isDepartment = false,
  parentTeamId
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const {user, role_id} = useAppSelector(state => state.auth);

  // Initialize form with team data if in edit mode
  React.useEffect(() => {
    if (editMode && teamData) {
      setName(teamData.name || '');
      setDescription(teamData.description || '');
    } else {
      // Reset form when opening in create mode
      setName('');
      setDescription('');
    }
    setError('');
  }, [editMode, teamData, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Team name is required');
      return;
    }

    try {
      setIsLoading(true);

      if (editMode && teamData) {
        // Update existing team
        await TeamService.updateTeam(teamData.id, {
          name,
          description
        });
        toast.success('Team updated successfully');
      } else {
        // Create new team
        const encryptionKey = "encrypted-team-key"; // This should be generated properly with encryption
        const member = {
          id: user?.id,
          role_id: role_id,
        }

        const teamData = {
          name,
          description,
          team_type: isDepartment ? 'department' : 'organization',
          parent_team_id: isDepartment ? parentTeamId : undefined,
          encrypted_team_key: encryptionKey,
          member: member
        };
        
        await TeamService.createTeam(teamData.name, teamData.description, teamData.encrypted_team_key, teamData.member);
        toast.success('Team created successfully');
      }
      
      onTeamCreated();
      resetForm();
    } catch (error) {
      console.error(`Error ${editMode ? 'updating' : 'creating'} team:`, error);
      setError(error instanceof Error ? error.message : `Failed to ${editMode ? 'update' : 'create'} team`);
      toast.error('Failed to create team');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setError('');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        resetForm();
        onClose();
      }}
      title={editMode 
        ? `Edit Team` 
        : `Create Team`
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 text-red-500 p-3 rounded-md text-sm">
            {error}
          </div>
        )}

        <Input
          label="Team Name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 resize-none"
            rows={4}
            placeholder="What is this team for?"
          ></textarea>
        </div>

        {isDepartment && parentTeamId && (
          <div className="bg-blue-50 text-blue-700 p-3 rounded-md text-sm">
            This team will be created within your organization.
          </div>
        )}

        <div className="flex justify-end space-x-3">
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              resetForm();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isLoading}>
            {editMode 
              ? `Update Team`
              : `Create Team`
            }
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateTeamModal;